import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as Handlebars from 'handlebars'; // Must use * for now

@Component({
	selector: 'app-production-data-view-detail',
	templateUrl: './production-data-view-detail.component.html',
	styleUrl: './production-data-view-detail.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionDataViewDetailComponent implements OnChanges, AfterViewInit {
	@Input() html: string | null;
	@Input() data: any;
	@ViewChild('iframe', { static: false })
	iframe: ElementRef<HTMLIFrameElement>;

	constructor() {
		Handlebars.registerHelper('findInArray', (array, propOrIndex, value, _) => {
			if (!Array.isArray(array)) return null;
			if (arguments.length === 3) return array[propOrIndex];
			return array.filter((item) => item[propOrIndex] === value);
		});
		Handlebars.registerHelper('injectSvg', (url) => {
			const xhr = new XMLHttpRequest();
			xhr.open('GET', url, false);
			xhr.send();
			return new Handlebars.SafeString(xhr.responseText);
		});
		Handlebars.registerHelper('concat', (...args) => {
			args.pop(); // remove the options object
			return args.join(''); // join the rest
		});
		Handlebars.registerHelper('roundNumber', (value, decimals, _) => {
			const number = Number.parseFloat(value);
			let decimalCount = Number.parseInt(decimals, 10);
			if (Number.isNaN(number)) return '';
			if (Number.isNaN(decimalCount) || decimalCount < 0) {
				decimalCount = 0;
			}
			return number.toFixed(decimalCount);
		});
		Handlebars.registerHelper('mathOperator', (left: number, operator: '+' | '-' | '*' | '/', right: number): number => {
			if (operator === '*') {
				return left * right;
			}
			if (operator === '+') {
				return left + right;
			}
			if (operator === '-') {
				return left - right;
			}
			if (operator === '/') {
				return left / right;
			}
			return Number.NaN;
		});
		Handlebars.registerHelper('ifEqual', function (a, b, options) {
			if (a === b) {
				return options.fn(this);
			}
			return options.inverse(this);
		});
		Handlebars.registerHelper('sumProperty', (array, prop) => {
			if (!Array.isArray(array)) return 0;
			return array.reduce((sum, item) => sum + (Number.parseFloat(item[prop]) || 0), 0);
		});
		Handlebars.registerHelper('formatTime', (milliseconds, millisecondsDigits) => {
			if (typeof milliseconds !== 'number') return 'NaN';
			millisecondsDigits = Number.parseInt(millisecondsDigits, 10);

			const hrs = Math.floor(milliseconds / 3600000);
			const mins = Math.floor((milliseconds % 3600000) / 60000);
			const secs = (milliseconds % 60000) / 1000;

			// workaround for toFixed rounding error.
			// https://www.sitepoint.com/number-tofixed-rounding-errors-broken-but-fixable/
			const split = secs.toString().split('.');
			const number = !split[1] ? split[0] : `${split.join('.')}1`;
			const secsString = Number.parseFloat(number).toFixed(millisecondsDigits);

			return (hrs > 0 ? `${String(hrs)}:` : '') + (mins > 0 ? `${String(mins).padStart(2, hrs > 0 ? '0' : '')}:` : hrs > 0 ? '00:' : '') + secsString.padStart(3 + millisecondsDigits, hrs > 0 || mins > 0 ? '0' : '');
		});
		Handlebars.registerHelper('firstPropertyOrDefault', (items, compareProp, value, outputProp, defaultValue) => {
			if (items) {
				const resolvePath = (object, path, defaultValue) =>
					path
						.split(/[.[\]'"]/)
						.filter((p) => p)
						.reduce((o, p) => (o ? o[p] : defaultValue), object);

				for (let i = 0; i < items.length; i++) {
					if (resolvePath(items[i], compareProp, '') === value) {
						return resolvePath(items[i], outputProp, defaultValue);
					}
				}
			}
			return defaultValue;
		});
	}

	// Show the default ItemOperation detail view when starting
	ngAfterViewInit(): void {
		this.writeDocument();
	}

	// When we change ItemOperation it should update the view
	ngOnChanges(changes: SimpleChanges): void {
		let shouldWrite = false;

		if (changes['data'] && !changes['data'].isFirstChange()) {
			const prevData = changes['data'].previousValue;
			const currentData = changes['data'].currentValue;
			if (!this.deepEqual(prevData, currentData)) {
				shouldWrite = true;
			}
		}

		// Compare html (string check)
		if (changes['html'] && !changes['html'].isFirstChange()) {
			const prevHtml = changes['html'].previousValue;
			const currentHtml = changes['html'].currentValue;
			if (prevHtml !== currentHtml) {
				shouldWrite = true;
			}
		}

		if (shouldWrite) {
			this.writeDocument();
		}
	}

	private writeDocument() {
		const iframeDoc = this.iframe?.nativeElement?.contentDocument;
		if (!iframeDoc) return;
		const compiled = Handlebars.compile(this.html)(this.data);
		iframeDoc.open();
		iframeDoc.write(compiled);
		iframeDoc.close();
	}

	private deepEqual(a: any, b: any): boolean {
		if (a === b) return true;
		if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
		const keysA = Object.keys(a);
		const keysB = Object.keys(b);
		if (keysA.length !== keysB.length) return false;
		return keysA.every((k) => this.deepEqual(a[k], b[k]));
	}
}
